import { JsonElementType, JsonObject, JsonProperty } from "ta-json";

import CultureInfo from "../../culture-info";
import { ILoadOption } from "./load-option";
import { LoadOption } from "./load-options";

/**
 * Specifies which cultures to load.
 */
export interface ICultureLoadOption extends ILoadOption {
    /**
     * Gets a list of cultures names that need to be loaded.
     * This is only useful when {@link LoadOption} is {@link LoadOption.Custom}.
     */
    cultures: Array<CultureInfo>;

    /**
     * Creates a deep clone of this instance.
     */
    deepClone(): ICultureLoadOption;
}

@JsonObject()
export class CultureLoadOption implements ICultureLoadOption {
    /**
     * Load no cultures.
     */
    static readonly None: Readonly<ICultureLoadOption> = new CultureLoadOption(LoadOption.None);

    /**
     * Load the default culture.
     */
    static readonly Default: Readonly<ICultureLoadOption> = new CultureLoadOption(LoadOption.Default);

    /**
     * Load all cultures.
     */
    static readonly All: Readonly<ICultureLoadOption> = new CultureLoadOption(LoadOption.All);

    @JsonProperty("load_option")
    loadOption: LoadOption = LoadOption.None;

    @JsonProperty("cultures")
    @JsonElementType(String)
    cultures: Array<CultureInfo> = [];

    constructor(param?: LoadOption | Array<CultureInfo> | Partial<ICultureLoadOption> | CultureLoadOption) {
        if (param == null) {
            return;
        } else if (param instanceof CultureLoadOption) {
            this.cultures = [...param.cultures];
            this.loadOption = param.loadOption;
        } else if (param instanceof Array) {
            this.cultures = [...param];
            this.loadOption = this.cultures.length ? LoadOption.Custom : LoadOption.None;
        } else if (typeof param === "object") {
            this.cultures = [...(param.cultures ?? [])];
            this.loadOption = param.loadOption != null ? param.loadOption : LoadOption.Custom;
        } else {
            this.loadOption = param;
        }
    }

    deepClone(): ICultureLoadOption {
        return new CultureLoadOption({ cultures: [...this.cultures], loadOption: this.loadOption });
    }
}
